import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import type { MessageKey } from '../i18n/messages'
import { DateLisible, Nombre } from '../components/Nombre'
import { Alert, Button, Card, DataTable, DescriptionItem, DescriptionList, FormActions, Grid, SelectField, Stack, TextField, useToast } from '../components/ui'
import type { Column } from '../components/ui'
import { ADDON_KINDS, growthApi } from './growthApi'
import type { AddonKind, SubscriptionGrowth } from './growthApi'

const nombre = (s: string) => Number(s.replace(/\s/g, '').replace(',', '.'))

/**
 * Options et remise d'un client, sur sa fiche (migration 0081).
 *
 * LES PLAFONDS AFFICHÉS SONT LES PLAFONDS EFFECTIFS : formule plus options en cours — ceux que
 * la garde d'abonnement applique. Une option se facture à chaque période ; la retirer demande
 * un motif. Le prix d'exploitations en plus, laissé vide, est celui de la formule.
 */
export function ClientGrowthCard({ organizationId }: { organizationId: number }) {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const token = session?.token ?? ''

  const [g, setG] = useState<SubscriptionGrowth | null>(null)
  const [sorte, setSorte] = useState<AddonKind>('farms')
  const [quantite, setQuantite] = useState('1')
  const [prix, setPrix] = useState('')
  const [code, setCode] = useState('')
  const [retrait, setRetrait] = useState<number | null>(null)
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const charger = useCallback(() => {
    if (token === '') return
    growthApi.clientGrowth(token, organizationId).then(setG).catch((cause: unknown) => notify(describeError(cause), 'erreur'))
  }, [token, organizationId, notify, describeError])

  useEffect(charger, [charger])

  const agir = async (action: () => Promise<unknown>, message: MessageKey) => {
    setEnvoi(true)
    try {
      await action()
      notify(t(message), 'succes')
      charger()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  const limite = (v: number | null | undefined, unite?: string) =>
    v === null || v === undefined ? t('subscription.unlimited') : <Nombre valeur={v} decimales={unite ? 2 : 0} unite={unite} />

  const colonnes: Column<SubscriptionGrowth['addons'][number]>[] = [
    { key: 'kind', header: t('growth.addons.kind'), render: (a) => t(`growth.addonKind.${a.kind}` as MessageKey) },
    { key: 'quantity', header: t('billing.detail.quantity'), numeric: true, render: (a) => <Nombre valeur={a.quantity} decimales={a.kind === 'area' ? 2 : 0} /> },
    { key: 'price', header: t('growth.addons.unitPrice'), numeric: true, render: (a) => <Nombre valeur={a.unitPrice} decimales={3} /> },
    { key: 'since', header: t('growth.addons.since'), render: (a) => <DateLisible valeur={a.startsOn} />, secondary: true },
    { key: 'end', header: '', render: (a) => <Button variant="discret" onClick={() => setRetrait(a.id)}>{t('growth.addons.end')}</Button> },
  ]

  return (
    <Card title={t('growth.addons.title')}>
      {g === null ? (
        <p className="text-muted">{t('common.loading')}</p>
      ) : (
        <Stack gap={3}>
          <DescriptionList>
            <DescriptionItem label={t('farms.title')}>{limite(g.limits?.maxFarms)}</DescriptionItem>
            <DescriptionItem label={t('users.title')}>{limite(g.limits?.maxUsers)}</DescriptionItem>
            <DescriptionItem label={t('plots.title')}>{limite(g.limits?.maxPlots)}</DescriptionItem>
            <DescriptionItem label={t('subscription.area')}>{limite(g.limits?.maxAreaHectares, 'ha')}</DescriptionItem>
          </DescriptionList>

          <DataTable columns={colonnes} rows={g.addons} rowKey={(a) => a.id} emptyLabel={t('growth.addons.empty')} />

          {retrait !== null && (
            <Alert tone="avertissement" title={t('growth.addons.endTitle')}>
              <Stack gap={2}>
                <TextField label={t('billing.credit.reason')} value={motif} onChange={setMotif} required />
                <FormActions>
                  <Button variant="secondaire" onClick={() => setRetrait(null)}>{t('common.cancel')}</Button>
                  <Button
                    variant="danger"
                    disabled={motif.trim() === ''}
                    pending={envoi}
                    onClick={() =>
                      agir(async () => {
                        await growthApi.endAddon(token, retrait, motif.trim())
                        setRetrait(null)
                        setMotif('')
                      }, 'growth.addons.ended')
                    }
                  >
                    {t('growth.addons.end')}
                  </Button>
                </FormActions>
              </Stack>
            </Alert>
          )}

          <strong>{t('growth.addons.add')}</strong>
          <Grid min="10rem">
            <SelectField
              label={t('growth.addons.kind')}
              value={sorte}
              onChange={(v) => setSorte(v as AddonKind)}
              options={ADDON_KINDS.map((k) => ({ value: k, label: t(`growth.addonKind.${k}` as MessageKey) }))}
            />
            <TextField label={t('billing.detail.quantity')} value={quantite} onChange={setQuantite} ltr required />
            <TextField label={t('growth.addons.unitPrice')} value={prix} onChange={setPrix} ltr hint={t('growth.addons.priceHint')} />
          </Grid>
          <FormActions>
            <Button
              variant="secondaire"
              pending={envoi}
              disabled={quantite.trim() === ''}
              onClick={() =>
                agir(() => growthApi.addAddon(token, organizationId, { kind: sorte, quantity: nombre(quantite), unitPrice: prix.trim() === '' ? null : nombre(prix) }), 'growth.addons.added')
              }
            >
              {t('growth.addons.add')}
            </Button>
          </FormActions>

          <strong>{t('growth.discount.title')}</strong>
          {g.discount !== null ? (
            <Stack gap={2}>
              <p>
                <span dir="ltr" className="font-medium">{g.discount.code}</span> — <Nombre valeur={g.discount.percentOff} decimales={0} unite="%" /> —{' '}
                {g.discount.cyclesRemaining === null ? t('growth.codes.forever') : <>{t('growth.discount.remaining')} <Nombre valeur={g.discount.cyclesRemaining} decimales={0} /></>}
              </p>
              <FormActions>
                <Button variant="danger" pending={envoi} onClick={() => agir(() => growthApi.removeDiscount(token, organizationId), 'growth.discount.removed')}>
                  {t('growth.discount.remove')}
                </Button>
              </FormActions>
            </Stack>
          ) : (
            <Grid min="12rem">
              <TextField label={t('growth.codes.code')} value={code} onChange={(v) => setCode(v.toUpperCase())} ltr />
              <FormActions>
                <Button
                  variant="secondaire"
                  disabled={code.trim() === ''}
                  pending={envoi}
                  onClick={() =>
                    agir(async () => {
                      await growthApi.applyDiscount(token, organizationId, code.trim())
                      setCode('')
                    }, 'growth.discount.applied')
                  }
                >
                  {t('growth.discount.apply')}
                </Button>
              </FormActions>
            </Grid>
          )}
        </Stack>
      )}
    </Card>
  )
}
